// @flow

import { Children, Component } from 'react';
import { findDOMNode } from 'react-dom';
import { connect } from 'react-redux';
import getCarouselData from 'app/common/react/components/common/Carousel/utils/getCarouselData';
import Impressions from 'app/packages/impression-logging';
import { track } from 'app/common/lib/react/analytics';
import { type CarouselPinData } from 'app/packages/pinterest-flow-types/pin';
import { type ImpressionStats } from 'app/packages/impression-logging/types';
import { type State as ReduxState } from 'app/common/redux/reducers/types';

type ImpressionType =
  | 'Article'
  | 'Board'
  | 'Pin'
  | 'Interest'
  | 'Search'
  | 'Story'
  | 'User'
  | 'News'
  | 'Guide'
  | 'HybridSearchHero';

type ReduxStateProps = {|
  carousel_data: ?CarouselPinData,
|};

type OwnProps = {|
  children?: any,
  componentType?: number,
  contextLogData?: Object,
  impressionType: ImpressionType,
  impressionAuxFields?: {
    richTypes?: Array<number>,
    imageURL?: string,
    videoIdStr?: ?string,
    clientTrackingParams?: string,
    isNativeVideoAndWatchable?: boolean,
    entered_query?: string,
  },
  inImpressionExp?: boolean,
  isPaused?: boolean,
  loggingId: ?string,
  objectIdStr?: string,
  slotIndex: number,
  trackCarousel?: boolean,
  viewData?: Object,
  viewParameter?: $Values<PCONST.thrift.ViewParameterType>,
  viewType?: $Values<PCONST.thrift.ViewType>,
|};

type Props = {|
  ...ReduxStateProps,
  ...OwnProps,
|};

export type IntersectionChange = {
  boundingClientRect: { top: number },
  intersectionRatio: number,
};

const impressionItems = {
  Article: {
    impressionType: 'articleImpressions',
    idType: 'articleIdStr',
    eventType: PCONST.thrift.EventType.ARTICLE_IMPRESSION_ONE_PIXEL,
  },
  Pin: {
    impressionType: 'pinImpressions',
    idType: 'pinIdStr',
    eventType: PCONST.thrift.EventType.PIN_IMPRESSION_ONE_PIXEL,
  },
  Board: {
    impressionType: 'boardImpressions',
    idType: 'boardIdStr',
    eventType: PCONST.thrift.EventType.BOARD_IMPRESSION_ONE_PIXEL,
  },
  Interest: {
    impressionType: 'topicImpressions',
    idType: 'topicIdStr',
    eventType: PCONST.thrift.EventType.TOPIC_IMPRESSION_ONE_PIXEL,
  },
  Search: {
    impressionType: 'searchImpressions',
    idType: 'term',
    eventType: PCONST.thrift.EventType.SEARCH_IMPRESSION_ONE_PIXEL,
  },
  Story: {
    impressionType: 'storyImpressions',
    idType: 'storyIdStr',
    eventType: PCONST.thrift.EventType.STORY_IMPRESSION_ONE_PIXEL,
  },
  HybridSearchHero: {
    impressionType: 'hybridSearchHeroImpressions',
    idType: 'hybridSearchHero',
    eventType: PCONST.thrift.EventType.SEARCH_HYBRID_SEARCH_HERO_IMPRESSION_ONE_PIXEL,
  },
  User: {
    impressionType: 'userImpressions',
    idType: 'userIdStr',
    eventType: PCONST.thrift.EventType.USER_IMPRESSION_ONE_PIXEL,
  },
  News: {
    impressionType: 'newsHubData',
    idType: 'newsIdStr',
    eventType: PCONST.thrift.EventType.NEWS_HUB_FEED_IMPRESSION_ONE_PIXEL,
  },
  Guide: {
    impressionType: 'guideImpressions',
    idType: 'term',
    eventType: PCONST.thrift.EventType.GUIDE_IMPRESSION_ONE_PIXEL,
  },
};

class ImpressionTracker extends Component<Props> {
  componentDidMount() {
    // set experiment status if not already done so
    if (this.props.inImpressionExp && !Impressions.inExperiment) {
      Impressions.setExperimentStatus(true);
    }
    if (!this.props.isPaused) {
      this.trackImpression();
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.isPaused && !this.props.isPaused) {
      this.trackImpression();
    }
  }

  componentWillUnmount() {
    if (this.node instanceof HTMLElement) {
      Impressions.stop(this.node);
    }
  }

  trackImpression = () => {
    try {
      this.node = findDOMNode(this);
      if (this.node instanceof HTMLElement) {
        Impressions.start(this.node)
          .onExitViewport(this.logImpression)
          .setDebugId(this.props.loggingId || 'unknown');
      }
    } catch (e) {
      window.console.error('Can only track impressions for type "HTMLElement"');
    }
  };

  logImpression = (stats: ImpressionStats) => {
    const {
      carousel_data,
      impressionType,
      trackCarousel,
      contextLogData,
      viewData,
      viewType,
      viewParameter,
      componentType,
      objectIdStr,
      loggingId,
      slotIndex,
      impressionAuxFields,
    } = this.props;
    const impressionItem = impressionItems[impressionType];
    const idTypeKey = impressionItem.idType;

    const forcedExitCode =
      stats.forcedExit && stats.forcedExit === 'removed'
        ? PCONST.thrift.ForcedExitReason.REMOVED
        : undefined;
    const impression = {
      endTime: stats.endTime,
      [idTypeKey]: loggingId,
      slotIndex,
      time: stats.startTime,
      renderDuration: stats.endTime - stats.startTime,
      ...impressionAuxFields,
      forcedExit: forcedExitCode,
    };

    // special trackCarousel flag because we have special logic resetting
    // carousel slot impression logging. don't want to overcount regular pin impressions
    if (!trackCarousel) {
      track(impressionItem.eventType, {
        viewData: viewData || {},
        view: viewType,
        viewParameter,
        component: componentType,
        objectIdStr,
        eventData: {
          [impressionItem.impressionType]: [impression],
        },
        ...contextLogData,
      });
    }

    if (carousel_data && trackCarousel) {
      const { carousel_slots, index, id } = carousel_data;
      track(PCONST.thrift.EventType.PIN_CAROUSEL_SLOT_IMPRESSION_ONE_PIXEL, {
        viewData: viewData || {},
        view: viewType,
        viewParameter,
        component: componentType,
        objectIdStr,
        eventData: {
          pinCarouselSlotImpressions: [
            {
              ...impression,
              carouselDataId: Number(id),
              carouselSlotId: carousel_slots[index] && Number(carousel_slots[index].id),
              slotIndex: index,
            },
          ],
        },
        ...contextLogData,
      });
    }
  };

  node: any | HTMLElement;

  render() {
    return Children.only(this.props.children);
  }
}

function mapStateToProps({ pins }: ReduxState, { loggingId }: OwnProps): ReduxStateProps {
  const pin = pins[loggingId || ''] || {};
  return {
    carousel_data: getCarouselData(pin),
  };
}

export default connect<Props, OwnProps, _, _, _, _>(
  mapStateToProps,
  () => ({}),
)(ImpressionTracker);
